import { z } from 'zod';
import { ProblemDetailsSchema, type CodeErreur } from '../common/index.js';

/**
 * Une route du contrat (architecture §3.1 « api ») : méthode, chemin, paramètres, requête, corps, réponses,
 * permission et action d'audit. Le constructeur `route()` applique les conventions du §3.2 :
 * erreurs problem+json, Idempotency-Key sur tout POST (sauf exemption justifiée), If-Match sur tout PATCH.
 */
export type Methode = 'get' | 'post' | 'patch' | 'put' | 'delete';

export interface Reponse {
  description: string;
  schema?: z.ZodType;
}

export interface RouteContrat {
  id: string;
  methode: Methode;
  chemin: string;
  tag: string;
  resume: string;
  description: string;
  parametres?: z.ZodObject;
  requete?: z.ZodObject;
  corps?: z.ZodType;
  reponses: Readonly<Record<number, Reponse>>;
  permission: string;
  public: boolean;
  actionAudit: string | null;
  idempotence: boolean;
  siMatch: boolean;
  erreurs: readonly CodeErreur[];
}

export interface DefinitionRoute {
  id: string;
  methode: Methode;
  chemin: string;
  tag: string;
  resume: string;
  description?: string;
  parametres?: z.ZodObject;
  requete?: z.ZodObject;
  corps?: z.ZodType;
  /** Statut de succès (200 par défaut, 201 pour une création, 202 pour un webhook, 204 sans corps). */
  statut?: 200 | 201 | 202 | 204;
  reponse?: z.ZodType;
  descriptionReponse?: string;
  permission: string;
  actionAudit?: string | null;
  erreurs?: readonly CodeErreur[];
}

/**
 * POST sans Idempotency-Key, chacun justifié : connexion, webhooks (dédoublonnés par l'identifiant
 * d'événement du fournisseur), opérations sans effet de bord ou déjà idempotentes par construction.
 */
export const EXEMPTIONS_IDEMPOTENCE: Readonly<Record<string, string>> = {
  'auth.connexion': 'Ouvre une session : rejouer la requête ne crée qu’une nouvelle session révocable.',
  'auth.lienMagique': 'Consomme un lien à usage compté : le compteur du lien sert de garde.',
  'auth.deconnexion': 'Fermer une session déjà fermée est sans effet.',
  'auth.otp.verifier': 'Vérification d’un code à usage unique, sans création.',
  'relances.simuler': 'Simulation pure, sans aucune écriture.',
  'genealogie.contacts.apercu': 'Aperçu d’un import de contacts : lecture et analyse du fichier, aucune écriture.',
  'sync.push': 'Chaque mutation porte son propre mutationId unique, garde d’idempotence du protocole.',
  'webhooks.jockalia': 'Dédoublonné par l’identifiant d’événement du fournisseur, signature vérifiée.',
  'webhooks.email': 'Dédoublonné par le Message-ID, signature vérifiée.',
  'webhooks.stripe': 'Dédoublonné par l’identifiant d’événement Stripe, signature vérifiée.',
  'webhooks.gocardless': 'Dédoublonné par l’identifiant d’événement GoCardless, signature vérifiée.',
  'webhooks.docuseal': 'Dédoublonné par l’identifiant de soumission et l’horodatage, signature vérifiée.',
};

const PROBLEME = (description: string): Reponse => ({ description, schema: ProblemDetailsSchema });

export function route(d: DefinitionRoute): RouteContrat {
  const estPublic = d.permission === 'public';
  const idempotence = d.methode === 'post' && !(d.id in EXEMPTIONS_IDEMPOTENCE);
  const siMatch = d.methode === 'patch';
  const statut = d.statut ?? (d.methode === 'post' && idempotence ? 201 : 200);
  const reponses: Record<number, Reponse> = {
    [statut]: { description: d.descriptionReponse ?? 'Succès.', ...(d.reponse ? { schema: d.reponse } : {}) },
  };
  if (d.corps || d.requete) reponses[400] = PROBLEME('Requête invalide (VALIDATION_FAILED).');
  if (!estPublic) {
    reponses[401] = PROBLEME('Session absente ou expirée (UNAUTHENTICATED).');
    reponses[403] = PROBLEME('Action non permise pour ce rôle (FORBIDDEN), ou action « en tant que » non confirmée.');
  }
  if (d.parametres) reponses[404] = PROBLEME('Ressource introuvable ou hors de votre périmètre (NOT_FOUND).');
  const erreurs = [...(d.erreurs ?? [])];
  if (siMatch && !erreurs.includes('VERSION_CONFLICT')) erreurs.push('VERSION_CONFLICT');
  if (idempotence || siMatch || erreurs.length > 0) reponses[409] = PROBLEME('Conflit métier : voir le code stable.');
  if (siMatch) reponses[428] = PROBLEME('En-tête If-Match manquant (PRECONDITION_REQUIRED).');
  return {
    id: d.id,
    methode: d.methode,
    chemin: d.chemin,
    tag: d.tag,
    resume: d.resume,
    description: d.description ?? d.resume,
    ...(d.parametres ? { parametres: d.parametres } : {}),
    ...(d.requete ? { requete: d.requete } : {}),
    ...(d.corps ? { corps: d.corps } : {}),
    reponses,
    permission: d.permission,
    public: estPublic,
    actionAudit: d.actionAudit ?? null,
    idempotence,
    siMatch,
    erreurs,
  };
}

export const versCheminExpress = (chemin: string): string => chemin.replace(/\{([a-zA-Z]+)\}/g, ':$1');
